import { PageViewModel, OptionsViewModel } from '@models/common.models';
import {
  VendorViewModel,
  VendorSearchViewModel,
  TradeViewModel,
  VendorPacketEventViewModel,
  PriceAgreementViewModel,
  ExcludedZipViewModel
} from '@models/Vendor';
import { BaseService } from '@services/BaseService';
import { TradeOptionsViewModel } from '@models/ServiceWorkOrder';

class VendorService extends BaseService {
  constructor() {
    super('vendor');
  }

  async createVendor(vendorVM: VendorViewModel): Promise<VendorViewModel> {
    const x = {
      ...vendorVM,
      w9Status: vendorVM.w9Status!.id
    };
    return this.fetch<VendorViewModel>('', {
      method: 'POST',
      body: JSON.stringify(x)
    });
  }

  getVendorAgreementTypes() {
    return this.fetch('agreementtypes', {
      method: 'GET'
    });
  }
  getVendorGroups() {
    return this.fetch('groups', {
      method: 'GET'
    });
  }
  getVendorStatuses() {
    return this.fetch('statuses', {
      method: 'GET'
    });
  }

  getVendorTypes() {
    return this.fetch('types', {
      method: 'GET'
    });
  }

  getCVIs() {
    return this.fetch('../cvi/cvis', {
      method: 'GET'
    });
  }

  getVendorW9Statuses() {
    return this.fetch('taxstatuses', {
      method: 'GET'
    });
  }

  getVendorDetails(id: number): Promise<VendorViewModel> {
    return this.fetch<VendorViewModel>(id.toString(), {
      method: 'GET'
    });
  }

  getCityZipOptions(stateAbbr: string): Promise<any> {
    return this.fetch<any>(`../vendor/zips?state=${stateAbbr}`, {
      method: 'GET'
    });
  }

  getTrades(): Promise<TradeOptionsViewModel[]> {
    return this.fetch<TradeOptionsViewModel[]>('../swo/trades', {
      method: 'GET'
    });
  }

  getVendorTrades(vendorId: number): Promise<TradeViewModel[]> {
    return this.fetch<TradeViewModel[]>(`../vendor/${vendorId}/trades`, {
      method: 'GET'
    });
  }

  getVendorTrade(vendorId: number, tradeId: number): Promise<TradeViewModel> {
    return this.fetch<TradeViewModel>(`../${vendorId}/trade/${tradeId}`, {
      method: 'GET',
      body: JSON.stringify(tradeId)
    });
  }

  getDocumentTypes(): Promise<OptionsViewModel[]> {
    return this.fetch<OptionsViewModel[]>(`../vendor/documentTypes`, {
      method: 'GET'
    });
  }

  saveVendorTrades(vendorId: number, vendorTrades: TradeViewModel[]): Promise<TradeViewModel[]> {
    return this.fetch<TradeViewModel[]>(`../vendor/${vendorId}/trades`, {
      method: 'POST',
      body: JSON.stringify(vendorTrades)
    });
  }

  saveIncludedZips(vendorId: number, includedZips: string[]): Promise<any> {
    return this.fetch<any>(`../vendor/${vendorId}/zips`, {
      method: 'POST',
      body: JSON.stringify(includedZips)
    });
  }

  deleteIncludedZips(vendorId: number, toBeDeletedZips: string[]): Promise<any> {
    return this.fetch<any>(`../vendor/${vendorId}/zips`, {
      method: 'DELETE',
      body: JSON.stringify(toBeDeletedZips)
    });
  }

  saveExcludedZips(tradeId: number, vendorId: number, excludedZips: ExcludedZipViewModel[]): Promise<any> {
    return this.fetch<any>(`../vendor/${vendorId}/excludedZips`, {
      method: 'POST',
      body: JSON.stringify(excludedZips)
    });
  }

  deleteExcludedZips(tradeId: number, vendorId: number, toBeDeletedZips: ExcludedZipViewModel[]): Promise<any> {
    return this.fetch<any>(`../vendor/${vendorId}/excludedZips`, {
      method: 'DELETE',
      body: JSON.stringify(toBeDeletedZips)
    });
  }

  getExcludedZips(vendorId: number, tradeId: number, tripCharges?: boolean): Promise<ExcludedZipViewModel[]> {
    let queryString = `?tradeId=${tradeId}` + (tripCharges ? `&tripCharges=${tripCharges}` : '');
    return this.fetch<Array<ExcludedZipViewModel>>(`../vendor/${vendorId}/excludedZips${queryString}`, {
      method: 'GET'
    });
  }

  updateVendorTrade(vendorId: number, vendorTrade: TradeViewModel): Promise<TradeViewModel> {
    return this.fetch<TradeViewModel>(`../vendor/${vendorId}/trade`, {
      method: 'PUT',
      body: JSON.stringify(vendorTrade)
    });
  }

  toggleTradeActivation(vendorId: number, vendorTradeId: number, activate: boolean): Promise<TradeViewModel> {
    return this.fetch<TradeViewModel>(`../vendor/${vendorId}/trade/${vendorTradeId}/${activate ? 'a' : 'dea'}ctivate`, {
      method: 'POST'
    });
  }

  searchPriceAgreements(
    id: number,
    query: string | null,
    pageNumber: number,
    pageSize: number,
    filterBy: string,
    orderBy: string,
    orderDirection: string,
    filters?: OptionsViewModel
  ): Promise<PageViewModel<any>> {
    const pn: number | undefined = pageNumber ? Math.max(0, pageNumber) : undefined;
    const ps: number | undefined = pageSize ? Math.max(0, pageSize) : undefined;

    let uri: string = `${id}/searchPriceAgreements`;

    let body = JSON.stringify({
      query: query,
      pageNumber: pn,
      pageSize: ps,
      filterBy: filterBy,
      orderBy: orderBy,
      orderDirection: orderDirection,
      filters: filters ? [filters] : undefined
    });

    return this.fetch<PageViewModel<PriceAgreementViewModel>>(uri, {
      method: 'POST',
      body: body
    });
  }

  searchVendors(
    query: string | null,
    pageSize?: number,
    pageNumber?: number,
    orderBy?: string,
    orderDirection?: string,
    trade?: string,
    type?: string,
    status?: string,
    group?: string
  ): Promise<PageViewModel<VendorSearchViewModel>> {
    const pn: number | undefined = pageNumber ? Math.max(0, pageNumber) : undefined;
    const ps: number | undefined = pageSize ? Math.max(0, pageSize) : undefined;

    const queryParameters: string[] = [];
    if (trade) {
      queryParameters.push(`trades=${encodeURIComponent(trade)}`);
    }
    if (type) {
      queryParameters.push(`type=${encodeURIComponent(type)}`);
    }
    if (status) {
      queryParameters.push(`status=${encodeURIComponent(status)}`);
    }
    if (group) {
      queryParameters.push(`group=${encodeURIComponent(group)}`);
    }

    let uri: string = 'search';
    if (queryParameters.length > 0) {
      uri = uri.concat('?', queryParameters.join('&'));
    }

    let body = JSON.stringify({
      query: query,
      pageNumber: pn,
      pageSize: ps,
      orderBy: orderBy,
      orderDirection: orderDirection,
      trade: trade,
      type: type
    });

    return this.fetch<PageViewModel<VendorSearchViewModel>>(uri, {
      method: 'POST',
      body: body
    });
  }

  updateVendor(vendorVM: VendorViewModel): Promise<VendorViewModel> {
    const x = {
      ...vendorVM,
      w9Status: (vendorVM.w9Status || { id: 0 }).id
    };
    return this.fetch<VendorViewModel>(vendorVM.id.toString(), {
      method: 'PUT',
      body: JSON.stringify(x)
    });
  }

  getVendorPacketEvents(vendorId: number): Promise<Array<VendorPacketEventViewModel>> {
    return this.fetch<Array<VendorPacketEventViewModel>>(`${vendorId}/packetEvents`, {
      method: 'GET'
    });
  }

  getVendorBrandCertifications(vendorId: number): Promise<Array<OptionsViewModel>> {
    return this.fetch<Array<OptionsViewModel>>(`${vendorId}/certifications`, {
      method: 'GET'
    });
  }

  updateVendorBrandCertifications(
    vendorId: number,
    brandCertifications: Array<OptionsViewModel>
  ): Promise<Array<OptionsViewModel>> {
    return this.fetch<Array<OptionsViewModel>>(`${vendorId}/certifications`, {
      method: 'POST',
      body: JSON.stringify(brandCertifications)
    });
  }

  getVendorCVIExceptions(vendorId: number): Promise<Array<OptionsViewModel>> {
    return this.fetch<Array<OptionsViewModel>>(`${vendorId}/cvi`, {
      method: 'GET'
    });
  }

  updateVendorCVIExceptions(vendorId: number, cvis: number[]): Promise<Array<OptionsViewModel>> {
    return this.fetch<Array<OptionsViewModel>>(`${vendorId}/cvi`, {
      method: 'POST',
      body: JSON.stringify(cvis)
    });
  }

  deleteVendorCVIExceptions(vendorId: number, cvis: number[]): Promise<Array<OptionsViewModel>> {
    return this.fetch<Array<OptionsViewModel>>(`${vendorId}/cvi`, {
      method: 'DELETE',
      body: JSON.stringify(cvis)
    });
  }

  getVendorBrandExceptions(vendorId: number): Promise<Array<OptionsViewModel>> {
    return this.fetch<Array<OptionsViewModel>>(`${vendorId}/brand`, {
      method: 'GET'
    });
  }

  updateVendorBrandExceptions(vendorId: number, brands: number[]): Promise<Array<OptionsViewModel>> {
    return this.fetch<Array<OptionsViewModel>>(`${vendorId}/brand`, {
      method: 'POST',
      body: JSON.stringify(brands)
    });
  }

  deleteVendorBrandExceptions(vendorId: number, brands: number[]): Promise<Array<OptionsViewModel>> {
    return this.fetch<Array<OptionsViewModel>>(`${vendorId}/brand`, {
      method: 'DELETE',
      body: JSON.stringify(brands)
    });
  }

  savePacketEvent(vendorPacketEvent: VendorPacketEventViewModel) {
    return this.fetch(`${vendorPacketEvent.vendorId}/packetEventSave`, {
      method: 'POST',
      body: JSON.stringify(vendorPacketEvent)
    });
  }

  savePriceAgreement(vendorId: number, agreement: PriceAgreementViewModel) {
    return this.fetch(`${vendorId}/priceAgreement`, {
      method: 'POST',
      body: JSON.stringify(agreement)
    });
  }

  searchZipCodes(
    vendorId: number,
    pageNumber: number,
    pageSize: number,
    query: string | null,
    excludeZips?: boolean
  ): Promise<PageViewModel<string>> {
    const pn: number = Math.max(1, pageNumber);
    const ps: number = Math.max(0, pageSize);

    let relativeUri = `${vendorId}/zipSearch?pageNumber=${pn}&pageSize=${ps}`;
    if (query && query !== '') {
      relativeUri += `&query=${query}`;
    }
    if (excludeZips) {
      relativeUri += `&excludeZips=true`;
    }

    return this.fetch<PageViewModel<string>>(relativeUri, {
      method: 'GET'
    });
  }
}

export const vendorService = new VendorService();



// WEBPACK FOOTER //
// ./src/_services/VendorService.ts